"use client";

import { useEffect, useRef, useState } from "react";
import { useBlockActions } from "@/contexts/blockContext";
import { useBoard } from "@/contexts/boardContext";
import { useWorkspaceContext } from "@/contexts/workspaceContext";
import { getWithAuth } from "@/lib/api-helpers";
import {
  loadDataSourcesMutation,
  upsertBlocksMutation,
  getDataSourceFromDB,
  toDataSource,
} from "@/indexedDB";
import type { Block } from "@/types/block";

// Per-widget pages fetch with SWR: context → IDB → API.
// Module-level set dedupes concurrent fetches for the same datasource id
// across multiple mounted widgets.
const inFlightFetches = new Set<string>();

export function useDatasourcePages(datasourceId?: string): Block[] {
  const { getBlock, upsertBlocks } = useBlockActions();
  const { dataSources, setDataSource, getNotesByDataSourceId } = useBoard();
  const { currentWorkspace } = useWorkspaceContext();
  // Ref so workspace identity churn doesn't retrigger the SWR effect.
  const workspaceIdRef = useRef<string | undefined>(currentWorkspace?._id);
  workspaceIdRef.current = currentWorkspace?._id;

  // Force a render after async upserts so the context lookup returns fresh pages.
  const [, forceRender] = useState(0);

  useEffect(() => {
    if (!datasourceId) return;

    // Hot path: already in context.
    if (dataSources[datasourceId]) {
      const existingPages = getNotesByDataSourceId(datasourceId);
      if (existingPages.length > 0) return;
    }

    if (inFlightFetches.has(datasourceId)) return;
    inFlightFetches.add(datasourceId);

    let cancelled = false;

    (async () => {
      try {
        // IDB first (instant for offline / repeat visits).
        try {
          const cachedRecord = await getDataSourceFromDB(datasourceId);
          if (!cancelled && cachedRecord) {
            const cachedDS = toDataSource(cachedRecord);
            setDataSource(datasourceId, cachedDS);
            forceRender((n) => n + 1);
          }
        } catch {
          // Non-fatal — fall through to API.
        }

        // API refresh.
        const res = (await getWithAuth(
          `/api/database/get-data-source/${datasourceId}`,
        )) as {
          success?: boolean;
          collection?: { dataSource?: any; blocks?: Block[] };
        };
        if (cancelled) return;
        if (!res?.success || !res.collection) return;

        if (res.collection.dataSource) {
          const ds = res.collection.dataSource;
          const dsId = ds._id || datasourceId;
          setDataSource(dsId, ds as any);
          if (workspaceIdRef.current) {
            loadDataSourcesMutation([ds], workspaceIdRef.current).catch(() => {});
          }
        }

        const pages = res.collection.blocks ?? [];
        if (pages.length > 0) {
          // Skip upsert for unchanged blocks to avoid re-render storms.
          const toUpsert = pages.filter((p) => {
            const existing = getBlock(p._id);
            if (!existing) return true;
            try {
              return JSON.stringify(existing) !== JSON.stringify(p);
            } catch {
              return true;
            }
          });
          if (toUpsert.length > 0) {
            await upsertBlocks(toUpsert);
          }
          upsertBlocksMutation(pages).catch(() => {});
          forceRender((n) => n + 1);
        }
      } catch (err) {
        if (!cancelled) {
          console.error("[useDatasourcePages] fetch failed for", datasourceId, err);
        }
      } finally {
        inFlightFetches.delete(datasourceId);
      }
    })();

    return () => {
      cancelled = true;
      inFlightFetches.delete(datasourceId);
    };
    // Context getters/setters are stable refs — intentionally omitted.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [datasourceId]);

  if (!datasourceId) return [];
  return getNotesByDataSourceId(datasourceId);
}
